'use strict';

import MixpanelService from '../mixpanel/mixpanel.service';
import Queue from '../../model/domain/queue';
import { OrganizationTypes } from '../../model/domain/organization';
import { AuthType } from '../auth/auth.service';
import { Locations } from '../mixpanel/mixpanel.service';

export default class AnalyticsService {

  constructor($log, $q, MixpanelService, LogRocketService) {
    'ngInject';

    this.$log = $log;
    this.$q = $q;

    /** @type {Deferred} */
    this._whenReadyDeferred = null;
    /** @type {Promise.<UserTokenInfo>} */
    this._whenReady = null;
    /** @type {MixpanelService} */
    this._mixpanelService = MixpanelService;
    /** @type {LogRocketService} */
    this._logRocketService = LogRocketService;
    /** @type <UserTokenInfo> */
    this._userData = null;

    this.logQueue = new Queue();
    this.isLoggedIn = false;
    this.isTeacher = false;
  }

  _addToAnalyticsQueue(eventInfo) {
    this.logQueue.enqueue(eventInfo);
  }

  _flushQueue() {
    while (this.logQueue.length > 0) {
      const firstItem = this.logQueue.dequeue();
      this._sendToMixpanel(firstItem);
    }
  }

  _sendToMixpanel(eventInfo) {
    const tag = eventInfo && eventInfo.eventTag;
    const properties = eventInfo && eventInfo.properties;
    const count = eventInfo && eventInfo.count;

    this._mixpanelService.send(tag, properties, count);
  }

  _activateMixpanelService(userData, abTestSegments) {
    this._mixpanelService.init(userData, abTestSegments);
  }

  identifyUserAsLoggedIn(userData, abTestSegments) {
    this._userData = userData;

    if (this._whenReadyDeferred) {
      this._whenReadyDeferred.resolve(this._userData);
      this._whenReadyDeferred = null;
    }
    else {
      this._whenReady = this.$q.resolve(this._userData);
    }

    if (userData.isTeacher) {
      this._activateMixpanelService(userData, abTestSegments);
      this.isTeacher = true;
      this.isLoggedIn = true;
      this._flushQueue();
      if (!this._logRocketService.initialized) {
        this._logRocketService.identify(userData.id, userData.name, userData.email, userData.username, userData.isPro);
      }
    } else {
      this.logQueue = new Queue();
    }
  }

  clearOutData() {
    this.isLoggedIn = false;
    this.isTeacher = false;
    this.logQueue = new Queue();
    this._mixpanelService.identify();
  }

  sendEvent(eventInfo) {
    if (!this.isLoggedIn && !this.isTeacher) {
      this._addToAnalyticsQueue(eventInfo);
    } else {
      this._flushQueue();
      this._sendToMixpanel(eventInfo);
    }
  }


  /**
   * Moved this over from the Mixpanel. It makes analytics wait to be logged until after
   * identify is called. Helps to address out of order logging
   * due to retrieving superproperties from the server
   */
  lockUntilIdentify() {
    if (!this._whenReadyDeferred) {
      this._whenReadyDeferred = this.$q.defer();
    }
    this._whenReady = this._whenReadyDeferred.promise;
  }

  setMixpanelProperty(property, value) {
    this._mixpanelService.setProperty(property, value);
  }

  /******** Mixpanel Events that have been moved to Analytics Page ********/

  /**
   * Teacher creates an assignment
   *
   * @param assignmentId {string}
   */
  assignmentCreated(assignmentId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'T added Assignment',
        properties: {
          assignmentId: assignmentId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('T added Assignment',
      {
        assignmentId: assignmentId
      },
      true
    );
  }

  assignmentLibraryOpened() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentLibrary',
        properties: {
          type: 'opened'
        },
        count: false
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('assignmentLibrary',
      {
        type: 'opened'
      },
      false
    );
  }

  /**
   * User send a chat message
   */
  chatSent() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'chatSent',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('chatSent', {}, true);
  }

  /**
   * When the class code dialog is opened
   *
   * @param rosterId {string}
   * @param assignmentId {string}
   * @param location {string} assignmentEdit | assignmentWatch
   */
  classCodeDialogOpened(rosterId, assignmentId, location) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'classCodeDialogOpened',
        properties: {
          rosterId: rosterId,
          assignmentId: assignmentId,
          location: location
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('classCodeDialogOpened', {
      rosterId: rosterId,
      assignmentId: assignmentId,
      location: location
    },
      true
    );
  }

  /**
   * User copied class code link in the class code dialog
   *
   * @param rosterId {string}
   * @param assignmentId {string}
   */
  classCodeLinkCopied(rosterId, assignmentId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'classCodeLinkCopied',
        properties: {
          rosterId: rosterId,
          assignmentId: assignmentId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('classCodeLinkCopied', {
      rosterId: rosterId,
      assignmentId: assignmentId
    },
      true
    );
  }

  /**
   * User copied class code link in the class code dialog for the first time after registration
   *
   * @param rosterId {string}
   * @param assignmentId {string}
   */
  classCodeLinkCopiedAfterRegistration(rosterId, assignmentId, firstTimeClicked) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'classCodeLinkCopiedAfterRegistration_clicked',
        properties: {
          rosterId: rosterId,
          assignmentId: assignmentId,
          firstTimeClicked : firstTimeClicked
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('classCodeLinkCopiedAfterRegistration_clicked', {
      rosterId: rosterId,
      assignmentId: assignmentId,
      firstTimeClicked : firstTimeClicked
    },
      true
    );
  }

  /**
   * User clicked open in new tab for the first time after registration
   *
   * @param rosterId {string}
   * @param assignmentId {string}
   */
  openNewTabAfterRegistration(rosterId, assignmentId, firstTimeClicked) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'openNewTabAfterRegistration_clicked',
        properties: {
          rosterId: rosterId,
          assignmentId: assignmentId,
          firstTimeClicked : firstTimeClicked

        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('openNewTabAfterRegistration_clicked', {
      rosterId: rosterId,
      assignmentId: assignmentId,
      firstTimeClicked : firstTimeClicked
    },
      true
    );
  }

  /**
   * User closed dialog for the first time after registration
   *
   * @param rosterId {string}
   * @param assignmentId {string}
   */
  closeWelcomeDialogAfterRegistration(rosterId, assignmentId, firstTimeClicked) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'closeWelcomeDialogAfterRegistration_clicked',
        properties: {
          rosterId: rosterId,
          assignmentId: assignmentId,
          firstTimeClicked : firstTimeClicked
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('closeWelcomeDialogAfterRegistration_clicked', {
      rosterId: rosterId,
      assignmentId: assignmentId,
      firstTimeClicked : firstTimeClicked
    },
      true
    );
  }

  /**
   * Published after each step in the account creation process
   *
   * @param step {string} started | name | location | referral | complete | schoolName
   */
  createAccountProgress(step) {
    this._whenReady.then((userData) => {

      if (!this.isLoggedIn && !this.isTeacher) {

        const eventInfo = {
          eventTag: 'createAccountProgress',
          properties: {
            completedStep: step,
            userType: userData.roles[0]
          }
        };
        return this.sendEvent(eventInfo);
      }

      this._mixpanelService.send('createAccountProgress', {
        completedStep: step,
        userType: this._userData.roles[0]
      });
    });
  }

  latestFeaturesOpened() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'latestFeaturesOpened',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('latestFeaturesOpened', {}, true);
  }

  /**
   * User taps the December special pricing button
   */
  decemberPricingOpened() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'december2021HolidayPricingOpened',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('december2021HolidayPricingOpened', {}, false);
  }

  /**
   * User taps get pro from the free trial countdown
   */
  getProFreeTrial() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'getProFromFreeTrialTapped',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('getProFromFreeTrialTapped', {}, false);
  }

  /**
   * User taps the Start a Pro School Free Trial button
   */
  freeTrialOpened() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'startFreeTrialTapped',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('startFreeTrialTapped', {}, false);
  }

  /**
   * @param type {string} viewed | assignmentClicked
   * @param profileId {string} user id of the owner of this profile
   * @param userId {string} user id of user visiting the page, or "anonymous"
   * @param [assignmentName] {string} name of assignment clicked
   */
  profileViewFunnel(type, profileId, userId, assignmentName) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'profileView',
        properties: {
          type,
          profileId,
          userId: userId || 'anonymous',
          assignmentName
        },
        count: false
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send(
      'profileView',
      {
        type,
        profileId,
        userId: userId || 'anonymous',
        assignmentName
      },
      false
    );
  }

  /**
   * Copy button clicked on view public assignment screen
   *
   * @param assignmentId {string}
   */
  publicAssignmentCopyClicked(assignmentId, organizationId = undefined, fromAssignmentLibrary = false) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'publicAssignmentCopyClicked',
        properties: {
          assignmentId,
          organizationId: fromAssignmentLibrary ? organizationId : undefined,
          fromAssignmentLibrary,
          userId: this._userData ? this._userData.id : 'anonymous'
        }
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('publicAssignmentCopyClicked', {
      assignmentId,
      organizationId: fromAssignmentLibrary ? organizationId : undefined,
      fromAssignmentLibrary,
      userId: this._userData ? this._userData.id : 'anonymous'
    }
    );
  }

  /**
   * Copy button clicked on view public folder screen
   *
   * @param folderId {string}
   */
  publicFolderCopyClicked(folderId, organizationId = undefined, fromAssignmentLibrary = false) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'publicFolderCopy_clicked',
        properties: {
          folderId,
          organizationId: fromAssignmentLibrary ? organizationId : undefined,
          fromAssignmentLibrary,
          userId: this._userData ? this._userData.id : 'anonymous'
        }
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('publicFolderCopy_clicked', {
      folderId,
      organizationId: fromAssignmentLibrary ? organizationId : undefined,
      fromAssignmentLibrary,
      userId: this._userData ? this._userData.id : 'anonymous'
    }
    );
  }

  /**
   * Copy button threw an error on view public folder screen
   *
   * @param folderId {string}
   */
  publicFolderCopyError(folderId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'publicFolderCopy_error',
        properties: {
          folderId
        }
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('publicFolderCopy_error', {
      folderId
    }
    );
  }

  /**
   * User visited a question on a public assignment
   *
   * @param assignmentId {string}
   * @param questionId {string}
   */
  publicAssignmentQuestionViewed(assignmentId, questionId, organizationId = undefined, fromAssignmentLibrary = false) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'publicAssignmentQuestionViewed',
        properties: {
          assignmentId,
          questionId,
          organizationId: fromAssignmentLibrary ? organizationId : undefined,
          fromAssignmentLibrary
        }
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('publicAssignmentQuestionViewed', {
      assignmentId,
      questionId,
      organizationId: fromAssignmentLibrary ? organizationId : undefined,
      fromAssignmentLibrary
    }
    );
  }

  /**
   * User visited the main public assignment view
   *
   * @param assignmentId {string}
   */
  publicAssignmentViewed(assignmentId, organizationId = undefined, fromAssignmentLibrary = false) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'publicAssignmentViewed',
        properties: {
          assignmentId,
          organizationId: fromAssignmentLibrary ? organizationId : undefined,
          fromAssignmentLibrary,
          userId: this._userData ? this._userData.id : 'anonymous'
        }
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('publicAssignmentViewed', {
      assignmentId,
      organizationId: fromAssignmentLibrary ? organizationId : undefined,
      fromAssignmentLibrary,
      userId: this._userData ? this._userData.id : 'anonymous'
    }
    );
  }

  /**
   * Created a roster complete on client
   *
   * @param location {string} assignmentEdit | rosterList
   */
  rosterCreatedFrom(location) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'rosterCreatedFrom',
        properties: {
          location: location
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('rosterCreatedFrom', {
      location: location
    },
      true
    );
  }

  /**
   * In roster edit view, clicked on link to Assignment List view (when no students are on the roster)
   */
  rosterToAssignments() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'rosterToAssignments',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('rosterToAssignments', {}, true);
  }

  rosterGradeExported() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'rosterGradeExported',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('rosterGradeExported', {}, true);
  }

  assignmentGradeExported() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentGradeExported',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentGradeExported', {}, true);
  }

  assignmentAnswerExported() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentAnswerExported',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentAnswerExported', {}, true);
  }

  assignmentLimitChecked(assignmentId, assignmentCount) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentLimitChecked',
        properties: {
          assignmentId: assignmentId,
          assignmentCount: assignmentCount
        }
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentLimitChecked', { assignmentId, assignmentCount });
  }

  /**
   * When a teacher opens the share assignment dialog, and clicks on the options. started = opened dialog, the rest pertain to specific buttons
   *
   * @param assignmentId {string}
   * @param location {string} assignmentEdit | assignmentList
   * @param step {string} started | withColleagues | withStudents | viaEmail | viaLink
   */
  shareAssignmentWorkflow(assignmentId, isFolder, location, step) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'shareAssignmentWorkflow',
        properties: {
          assignmentId: assignmentId,
          isFolder: isFolder,
          location: location,
          step: step
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('shareAssignmentWorkflow', {
      assignmentId: assignmentId,
      isFolder: isFolder,
      location: location,
      step: step
    },
      true
    );
  }

  /**
   * User clicks a button to navigate to whole class view
   *
   * @param assignmentId {string}
   * @param location {string} assignmentEdit-top | assignmentEdit-roster | assignmentList
   */
  tappedViewStudentWork(assignmentId, location) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'Tapped ViewStudentWork',
        properties: {
          assignmentId: assignmentId,
          location: location
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('Tapped ViewStudentWork', {
      assignmentId: assignmentId,
      location: location
    },
      true
    );
  }

  /**
   * WCV clicked a T edit slide go to edit mode
   *
   * @param assignmentId {string}
   * @param questionId {string}
   */
  wcvToEditQuestion(assignmentId, questionId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'wcvToEditQuestion',
        properties: {
          assignmentId: assignmentId,
          questionId: questionId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send('wcvToEditQuestion', {
      assignmentId: assignmentId,
      questionId: questionId
    },
      true
    );
  }

  experiment(name) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `experiment.${name}`,
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send(
      `experiment.${name}`,
      {},
      false
    );
  }

  /**
   * Any account is created (teacher, parent, student, anon student)
   */
  accountCreated(authType = AuthType.PASSWORD) {
    this._whenReady.then((userData) => {


      if (!this.isLoggedIn && !this.isTeacher) {

        const eventInfo = {
          eventTag: 'accountCreated',
          properties: {
            userType: userData.roles[0],
            authType: authType
          }
        };
        return this.sendEvent(eventInfo);
      }

      this._mixpanelService.send('accountCreated', {
        userType: this._userData.roles[0],
        authType: authType
      });
    });
  }

  /**
   * @param questionNumber {number} the question number the user is viewing
   */
  sessionQuestionViewed(questionNumber) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'sessionQuestionViewed',
        properties: { questionNumber },
        count: true
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send(
      'sessionQuestionViewed',
      { questionNumber },
      true
    );
  }

  /**
   * @param type {string} the type of setting that was toggled
   * @param value {boolean} the value of the setting
   */
  sessionSettingToggled(type, value) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'sessionSettingToggled',
        properties: {
          type,
          value
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'sessionSettingToggled',
      {
        type,
        value
      },
      true
    );
  }

  /**
   * @param source {string} content | feedback | session_question
   */
  stickerSidenavOpened(source) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'stickerSidenavOpened',
        properties: { source },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'stickerSidenavOpened',
      { source },
      true
    );
  }

  /**
   * @param source {string} content | feedback | session_question
   * @param type {string} custom | stock | none
   * @param name {string} name of the stock sticker if used
   */
  stickerSaved(source, type, name) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'stickerSaved',
        properties: { source, type, name },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'stickerSaved',
      { source, type, name },
      true
    );
  }

  /**
   * @param source {string} content | feedback | session_question
   * @param type {string} custom | stock | none
   * @param name {string} name of the stock sticker if used
   */
  stickerPlaced(source, type, name) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'stickerPlaced',
        properties: { source, type, name },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'stickerPlaced',
      { source, type, name },
      true
    );
  }

  /**
   * @param step {string} paywall | learnMore | enterPayment | complete
   * @param [subscriptionPlan] {string} ex: annual-plus-v1
   * @param [contractType] {string} antares | niagara
   * @param [source] {string} sidebar_button | new_folder | highlighter | multiple_choice
   */
  subscriptionFunnel(step, subscriptionPlan, contractType, source) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'subscriptionFunnel',
        properties: {
          step,
          subscriptionPlan,
          contractType,
          source
        },
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'subscriptionFunnel',
      {
        step,
        subscriptionPlan,
        contractType,
        source
      },
      false
    );
  }

  /**
   * @param view {string}
   * @param [value] {string}
   */
  teacherHelpCenter(view, value) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'teacherHelpCenter',
        properties: {
          view,
          value
        },
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'teacherHelpCenter',
      {
        view,
        value
      },
      false
    );
  }

  userDeleted() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'userDeleted',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('userDeleted', {}, false);
  }

  /**
   * @param [orgType] the type of organization the user is confirming membership in
   */
  userOrgConfirmed(orgType = OrganizationTypes.School) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'userOrgConfirmed',
        properties: { orgType },
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('userOrgConfirmed', { orgType }, false);
  }

  /**
   * Teacher taps "Email Us" from the district/school renewal warning banner
   *
   */
  renewalBannerEmailInteraction() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'renewalBannerEmailInteraction',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('renewalBannerEmailInteraction', {}, false);
  }

  /**
   * Teacher clicks chevron dropdown for more details on pro trial
   *
   */
  proTrialDetailsOpened() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'nav:proTrialDetails_opened',
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('nav:proTrialDetails_opened', { userId, userEmail }, true);
  }

  /**
   * Teacher clicks `Refer a peer` button and pro trial referral email dialog is opened
   *
   */
  proTrialReferralOpened(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:proTrialReferralEmail_opened`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:proTrialReferralEmail_opened`, { userId, userEmail }, true);
  }

  /**
   * Teacher taps "X" on refer a peer email dialog to dismiss
   *
   */
  proTrialReferralDismissed(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:proTrialReferralEmail_dismissed`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:proTrialReferralEmail_dismissed`, { userId, userEmail }, true);
  }

  /**
   * Teacher sends email with refer a peer email dialog
   *
   */
  proTrialReferralSent(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:proTrialReferralEmail_sent`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:proTrialReferralEmail_sent`, { userId, userEmail }, true);
  }

  /**
   * Teacher sends metrics email
   *
   */
  metricsEmailSent(orderId) {
    this._mixpanelService.send('metrics:metricsEmail_sent', { orderId }, true);
  }

  /**
   * Teacher taps "X" from the pro trial welcome overlay
   *
   */
  proTrialOverlayDismissed() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'proTrialOverlay:congratsDialog_dismissed',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('proTrialOverlay:congratsDialog_dismissed', {}, false);
  }

  /**
   * Teacher taps "Show All Benefits" from the pro trial welcome overlay
   *
   */
  proTrialOverlayBenefits() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'proTrialOverlay:showBenefits_opened',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('proTrialOverlay:showBenefits_opened', {}, false);
  }

  /**
   * Teacher clicks `Refer a peer` button and referral email dialog is opened existing pro school or district
   *
   */
  proSchoolOrDistrictReferralOpened(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:proSchoolOrDistrictReferralEmail_opened`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:proSchoolOrDistrictReferralEmail_opened`, { userId, userEmail }, true);
  }

  /**
   * Teacher taps "X" on refer a peer email dialog to dismiss
   *
   */
  proSchoolOrDistrictReferralDismissed(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:proSchoolOrDistrictReferralEmail_dismissed`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:proSchoolOrDistrictReferralEmail_dismissed`, { userId, userEmail }, true);
  }

  /**
   * Teacher sends email with refer a peer email dialog
   *
   */
  proSchoolOrDistrictReferralSent(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:proSchoolOrDistrictReferralEmail_sent`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:proSchoolOrDistrictReferralEmail_sent`, { userId, userEmail }, true);
  }

  /**
   * Teacher clicks "SELECT COLLEAGUES" from the share order dialog
   *
   */
  selectPeersDialogOpened(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:selectPeersDialog_opened`,
        properties: {
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:selectPeersDialog_opened`, { userId }, true);
  }

  /**
   * Teacher clicks X from the select peers dialog
   *
   */
  selectPeersDialogDismissed(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:selectPeersDialog_dismissed`,
        properties: {
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:selectPeersDialog_dismissed`, { userId }, true);
  }

  /**
   * Teacher clicks SEND EMAIL from the select peers dialog
   *
   */
  selectPeersDialogReferralSent(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:selectPeersDialog_sent`,
        properties: {
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:selectPeersDialog_sent`, { userId }, true);
  }


  /**
   * Admin clicks "REMIND TEACHERS TO JOIN CLASSKICK" from the nav
   *
   */
  remindInactiveTeachersDialogOpened(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:remindInactiveTeachersDialog_opened`,
        properties: {
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:remindInactiveTeachersDialog_opened`, { userId }, true);
  }

  /**
   * Admin clicks X from the remind inactive teachers dialog
   *
   */
  remindInactiveTeachersDialogDismissed(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:remindInactiveTeachersDialog_dismissed`,
        properties: {
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:remindInactiveTeachersDialog_dismissed`, { userId }, true);
  }

  /**
   * Admin checks/unchecks the pre-populated inactive teachers and mdr email select box
   *
   */
  remindInactiveTeachersDialogEmailsCheckbox(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:remindInactiveTeachersDialogEmailsCheckbox_clicked`,
        properties: {
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:remindInactiveTeachersDialogEmailsCheckbox_clicked`, { userId }, true);
  }

  /**
   * Admin selects contract dropdown from the remind inactive teacher dialog
   *
   */
  remindInactiveTeachersDialogEmailsContractDropdown(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:remindInactiveTeachersDialogEmailsContractDropdown_clicked`,
        properties: {
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:remindInactiveTeachersDialogEmailsContractDropdown_clicked`, { userId }, true);
  }

  /**
   * Admin clicks SEND EMAIL from the remind inactive teacher dialog
   *
   */
  remindInactiveTeachersDialogReferralSent(clickedFrom, contractId, emailsSentCount) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: `${clickedFrom}:remindInactiveTeachersDialog_sent`,
        properties: {
          userId: userId,
          contractId: contractId,
          emailsSentCount: emailsSentCount
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:remindInactiveTeachersDialog_sent`, { userId: userId, contractId: contractId, emailsSentCount: emailsSentCount }, true);
  }

  /**
   * Teacher selects from the org drop down in assignment library
   *
   */
  assignmentLibraryDropdownSelected(organizationId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentLibrary:orgDropdown_selected',
        properties: { organizationId },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentLibrary:orgDropdown_selected', { organizationId }, true);
  }

  /**
   * Teacher opens the org drop down in assignment library
   *
   */
  assignmentLibraryDropdownOpened(organizationId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentLibrary:orgDropdown_opened',
        properties: { organizationId },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentLibrary:orgDropdown_opened', { organizationId }, true);
  }

  addedNewPassword(userId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'profile:accountPassword_added',
        properties: { userId },
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('profile:accountPassword_added', { userId }, false);
  }

  goToHiddenRostersPage() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'roster:hidden_rosters_clicked',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('roster:hidden_rosters_clicked', {}, true);
  }

  hideRoster(syncRoster) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'roster:hide_roster',
        properties: {
          synced: syncRoster,
          hidden: true
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('roster:hide_roster', { synced: syncRoster, hidden: true }, true);
  }

  unhideRoster(syncRoster) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'roster:unhide_roster',
        properties: {
          synced: syncRoster,
          hidden: false
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('roster:unhide_roster', { synced: syncRoster, hidden: false }, true);
  }

  /**
   * User lands on the order detail page
   *
   * @param orderId {string} the order id
   * */
  viewOrderDetails(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:page_viewed',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:page_viewed', { orderId, userId }, true);
  }

  /**
   * User clicks email button on the order detail page
   *
   * @param orderId {string} the order id
   * */
  emailQuote(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:email_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:email_clicked', { orderId, userId }, true);
  }


  /**
   * Tracks when district order is auto-generated on page load
   *
   * @param orderId {string} the order id
   * @param organizationId {string} the district organizationId
   * */
  districtOrderGenerated(orderId, organizationId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderCreation:district_autogenerated',
        properties: {
          orderId,
          organizationId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderCreation:district_autogenerated', { orderId, organizationId, userId }, true);
  }


  /**
   * User clicks email button on the metrics page
   *
   * @param organizationId {string} the organization id
   * @param orderId the orderId
   * */
  emailMetrics(organizationId, orderId) {
    this._mixpanelService.send('metrics:email_clicked', { organizationId, orderId }, true);
  }


  /**
   * User clicks print button on the order detail page
   *
   * @param orderId {string} the order id
   * */
  printQuote(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:print_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:print_clicked', { orderId, userId }, true);
  }

  /**
   * User clicks cancel quote button on the order detail page
   *
   * @param orderId {string} the order id
   * */
  cancelQuote(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:cancelQuote_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:cancelQuote_clicked', { orderId, userId }, true);
  }

  /**
   * User clicks "Change This" button on the order detail page to change contract dates
   *
   * @param orderId {string} the order id
   * */
  changeOrderDates(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:changeThis_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:changeThis_clicked', { orderId, userId }, true);
  }

  /**
   * User clicks FAQs button on the order detail page
   *
   * @param orderId {string} the order id
   * */
  goToFaqs(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:faqs_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:faqs_clicked', { orderId, userId }, true);
  }

  /**
   * User clicks "Pay With Credit Card" button on the order detail page
   *
   * @param orderId {string} the order id
   * */
  payWithCreditCard(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:payWithCreditCard_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:payWithCreditCard_clicked', { orderId, userId }, true);
  }

  /**
   * User clicks "Submit A Purchase Order" button on the order detail page
   *
   * @param orderId {string} the order id
   * */
  submitPurchaseOrder(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:submitPurchaseOrder_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:submitPurchaseOrder_clicked', { orderId, userId }, true);
  }

  /**
   * Teacher clicks `Copy Invite Action` in the admin invites inactive teachers dialog
   *
   */
  adminInviteTextCopied(clickedFrom, copiedElem) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    const tag = `remindInactiveTeachersDialog:${copiedElem}`;

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:${tag}_clicked`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:${tag}_clicked`, { userId, userEmail }, true);
  }

  /**
   * Teacher clicks `Copy Invite Text` in the referral dialog
   *
   */
  inviteTextCopied(clickedFrom, isProSchoolDistrictReferral) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    const tag = isProSchoolDistrictReferral ? 'proSchoolOrDistrictReferralEmailInviteButton' : 'proTrialReferralEmailInviteButton';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:${tag}_clicked`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:${tag}_clicked`, { userId, userEmail }, true);
  }

  /**
   * Teacher taps "X" on share order dialog to dismiss
   *
   */
  shareOrderDialogDismissed(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:shareOrderDialog_dismissed`,
        properties: {
          userId,
          userEmail
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:shareOrderDialog_dismissed`, { userId, userEmail }, true);
  }

  /**
   * Teacher taps "X" on share metrics dialog to dismiss
   *
   */
  shareMetricsDialogDismissed(orderId) {
    this._mixpanelService.send('metrics:shareMetricsDialog_dismissed', { orderId }, true);
  }

  /**
   * Teacher sends email via share order dialog
   *
   */
  shareOrderDialogSent(clickedFrom, sentTo) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:shareOrderDialog_sent`,
        properties: {
          userId,
          userEmail,
          sentTo
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:shareOrderDialog_sent`, { userId, userEmail, sentTo }, true);
  }

  /**
   * Teacher opens the share order dialog
   *
   */
  shareOrderDialogOpened(clickedFrom) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:shareOrderDialog_opened`,
        properties: {
          userId,
          userEmail,
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:shareOrderDialog_opened`, { userId, userEmail }, true);
  }
  /**
   * New share button on assignment edit clicked
   *
   */
  newShareButtonClicked(assignmentId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentEdit:newShareButton_clicked',
        properties: {
          assignmentId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentEdit:newShareButton_clicked', {
      assignmentId,
      userId
    }, true);
  }

  copyFromAssignmentLibrary(assignmentId, organizationId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentLibrary:addButton_clicked',
        properties: {
          assignmentId,
          userId,
          organizationId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentLibrary:addButton_clicked', {
      assignmentId,
      userId,
      organizationId
    }, true);
  }

  customEmailReferred() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'selectPeersDialog:customEmail_referred',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('selectPeersDialog:customEmail_referred', {}, true);
  }

  mdrEmailReferred() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'selectPeersDialog:mdrEmail_referred',
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('selectPeersDialog:mdrEmail_referred', {}, true);
  }

  copyOrderLink(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetail:copyOrderLink_clicked',
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetail:copyOrderLink_clicked', {
      orderId,
      userId
    }, true);
  }

  /**
   * User clicks `Copy Text` in the share order dialog
   *
   */
  copyShareOrderText(clickedFrom, orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickedFrom}:shareOrderDialogCopyTextButton_clicked`,
        properties: {
          orderId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:shareOrderDialogCopyTextButton_clicked`, {
      orderId,
      userId
    }, true);
  }

  /**
   * User closes the free trial conversion banner
   *
   * @param orderId {string} the order id
   * */
  closeFreeTrialConversionBanner(orderId, organizationId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'header:freeTrialConversionBanner_closed',
        properties: {
          orderId,
          organizationId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('header:freeTrialConversionBanner_closed', { orderId, organizationId, userId }, true);
  }

  /**
   * User closes the renewal conversion banner
   *
   * @param orderId {string} the order id
   * */
  closeRenewalConversionBanner(orderId, organizationId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'header:renewalConversionBanner_closed',
        properties: {
          orderId,
          organizationId,
          userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('header:renewalConversionBanner_closed', { orderId, organizationId, userId }, true);
  }

  /**
   * Teacher taps "X" from the pro trial conversion overlay
   *
   */
  proTrialConversionOverlayDismissed() {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'proTrialConversionOverlay:conversionDialog_dismissed',
        properties: {},
        count: false
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('proTrialConversionOverlay:conversionDialog_dismissed', {}, false);
  }

  /**
   * Teacher opens or closes the "Cover Slide" wand from View Work page
   *
   */
  stopWandToggled(action) {
    let eventTag = `assignmentViewWork:stopWand_${action ? 'opened' : 'closed'}`;
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag,
        properties: {},
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(eventTag, {}, true);
  }

  /**
   * Teacher opens the quote iframe from nav bar
   *
   */
  openQuotePageIframe(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'nav:quotePageIframe_opened',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('nav:quotePageIframe_opened', { userId, orderId }, true);
  }

  /**
   * Teacher closes the quote iframe from nav bar
   *
   */
  closedQuotePageIframe(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'nav:quotePageIframe_closed',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('nav:quotePageIframe_closed', { userId, orderId }, true);
  }

  /**
   * Teacher goes to quote page from the iframe
   *
   */
  goToQuotePageFromIframe(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'quotePageIframe:goToQuote_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('quotePageIframe:goToQuote_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on the W9 link on the PDF order
   *
   */
  goToW9(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:w9Link_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:w9Link_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on the PO link on the PDF order
   *
   */
  goToOrderLink(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:orderLink_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:orderLink_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on the terms of service link on the PDF order
   *
   */
  goToTOS(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:termsOfServiceLink_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:termsOfServiceLink_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on the privacy policy link on the PDF order
   *
   */
  goToPrivacyPolicy(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:privacyPolicyLink_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:privacyPolicyLink_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on the third party link on the PDF order
   *
   */
  goToThirdPartyLink(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:thirdPartyLink_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:thirdPartyLink_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on the research link on the PDF order
   *
   */
  goToResearchLink(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:researchLink_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:researchLink_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on the watch video link on the PDF order
   *
   */
  goToVideoLink(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:videoLink_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:videoLink_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher clicks on reserve price
   *
   */
  reservePrice(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:reservePrice_clicked',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:reservePrice_clicked', { userId, orderId }, true);
  }

  /**
   * Teacher closes the reserve price dialog
   *
   */
  closeReservePrice(orderId) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'orderDetails:reservePrice_dismissed',
        properties: {
          userId,
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('orderDetails:reservePrice_dismissed', { userId, orderId }, true);
  }

  /**
   * Teacher clicks link to go to order details page
   *
   */
  goesToOrderDetailsPage(clickFrom, orderId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickFrom}:orderLink_clicked`,
        properties: {
          orderId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickFrom}:orderLink_clicked`, { orderId }, true);
  }

  /**
   * Teacher clicks on student preview button
   *
   */
  openStudentPreview(clickFrom, assignmentId) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: `${clickFrom}:studentPreview_clicked`,
        properties: {
          assignmentId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickFrom}:studentPreview_clicked`, { assignmentId }, true);
  }

  /**
   * @param viewType {string} wcv | sqv - Whole Class View or Single Question View
   */
  bulkUpdateOpened(viewType) {
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentViewWork:bulkUpdateMenu_opened',
        properties: {
          viewType
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'assignmentViewWork:bulkUpdateMenu_opened',
      {
        viewType
      },
      true
    );
  }

  /**
   * @param selected {string} selected bulk update option
   * @param viewType {string} wcv | sqv - Whole Class View or Single Question View
   */
  bulkUpdateSelected(selected, viewType) {
    const option = selected;
    if (!this.isLoggedIn && !this.isTeacher) {

      const eventInfo = {
        eventTag: 'assignmentViewWork:bulkUpdateOption_selected',
        properties: {
          selected: option,
          viewType
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'assignmentViewWork:bulkUpdateOption_selected',
      {
        selected: option,
        viewType
      },
      true
    );
  }

  /**
   * @param viewType {string} wcv | sqv - Whole Class View or Single Question View
   * @param feedbackType {string} type of feedback (sticker | cover slides)
   * @param selectedBulkUpdateOption {string} selected bulk update option
   * @param feedbackTotal {number} Total number of feedback applied
   * @param studentTotal {number} Total number of students
   * @param sticker {UserSticker | null}
   */
  bulkUpdateApplied({
    viewType,
    feedbackType,
    selectedBulkUpdateOption,
    feedbackTotal,
    studentTotal,
    sticker = null
  }) {
    let eventInfo = {
      eventTag: 'assignmentViewWork:bulkUpdate_applied',
      properties: {
        viewType,
        feedbackType,
        selectedBulkUpdateOption,
        feedbackTotal,
        studentTotal,
      },
      count: true
    };

    if (sticker) {
      eventInfo.properties.stickerType = sticker.imageType;
      eventInfo.properties.stickerName = sticker.imageName;
    }

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'assignmentViewWork:bulkUpdate_applied',
      {
        ...eventInfo.properties
      },
      true
    );
  }

  /**
   * @param questionId {string} question id
   * @param activity {string} type of activity (added | removed)
   */
  coverSlideActivity(location, questionId, activity) {
    let eventInfo = {
      eventTag: `${location}:coverSlide_${activity}`,
      properties: {
        questionId
      },
      count: true
    };

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `${location}:coverSlide_${activity}`,
      {
        ...eventInfo.properties
      },
      true
    );
  }

  infoIconClicked(location, component) {
    let eventInfo = {
      eventTag: `${location}:${component}InfoIcon_clicked`,
      properties: {},
      count: true
    };

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `${location}:${component}InfoIcon_clicked`,
      {
        ...eventInfo.properties
      },
      true
    );
  }

  /**
   * User clicks on the "What's a class code?" link
   *
   */
  classCodeDefinitionClicked(clickedFrom) {
    let eventInfo = {
      eventTag: `${clickedFrom}:classCodeDefinition_clicked`,
      properties: {},
      count: true
    };

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `${clickedFrom}:classCodeDefinition_clicked`,
      {
        ...eventInfo.properties
      },
      true
    );
  }

  /**
   * User clicks on button to recommend assignment
   *
   */
  recommendAssignment(rosterId, assignmentId, isPromoted) {
    const status = isPromoted ? 'unrecommend' : 'recommend';
    let eventInfo = {
      eventTag: `assignmentViewWork:${status}Assignment_clicked`,
      properties: {
        rosterId,
        assignmentId
      },
      count: true
    };

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `assignmentViewWork:${status}Assignment_clicked`,
      {
        ...eventInfo.properties
      },
      true
    );
  }

  /**
   * Teacher clicks link/button to go to order page
   *
   */
  goToOrderPage(clickFrom, organizationId, assignmentCount = null) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: `${clickFrom}:getPro_clicked`,
      properties: {
        userId,
        assignmentCount,
        organizationId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickFrom}:getPro_clicked`,
      {
        ...eventInfo.properties
      }, true);
  }


  /**
   * User clicks on creating assignment menu
   */
  clickAddAssignmentAction() {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: 'addAssignmentAction_clicked',
      properties: {
        location: 'header',
        userId,
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('addAssignmentAction_clicked',
      {
        ...eventInfo.properties
      }, true);
  }


  /**
   * User clicks on creating a new blank assignment
   */
  createNewBlankAssignment() {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: 'createNewBlankAssignment_clicked',
      properties: {
        location: 'header',
        userId,
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('createNewBlankAssignment_clicked',
      {
        ...eventInfo.properties
      }, true);
  }


  /**
   * User clicks on creating a new assignment via file upload
   */
  createNewAssignmentByFileUpload() {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: 'createNewAssignmentByFileUpload_clicked',
      properties: {
        location: 'header',
        userId,
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('createNewAssignmentByFileUpload_clicked',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User clicks on Browse Assignment Library from header menu
   */
  clickOnBrowseAssignmentLibrary() {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: 'browseAsssignmentLibrary_clicked',
      properties: {
        location: 'header',
        userId,
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('browseAsssignmentLibrary_clicked',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User clicks on Create Folder from header menu
   */
  createFolderAction() {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: 'createFolderAction_clicked',
      properties: {
        location: 'header',
        userId,
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('createFolderAction_clicked',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User closes the assignment limit notification banner
   *
   * */
  closeAssignmentLimitNotificationBanner(organizationId, assignmentCount) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: 'header:assignmentLimitNotificationBanner_closed',
      properties: {
        organizationId,
        assignmentCount,
        userId,
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('header:assignmentLimitNotificationBanner_closed',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User opens or closes assignment limit paywall dialog
   *
   * */
  openOrCloseAssignmentPaywall(
    clickedFrom,
    action,
    assignmentCount,
    isFromDisabledAssignment,
    assignmentId
  ) {
    const userId = this._userData ? this._userData.id : 'anonymous';

    const eventInfo = {
      eventTag: `${clickedFrom}:assignmentLimitPaywallDialog_${action}`,
      properties: {
        userId,
        assignmentCount,
        isFromDisabledAssignment,
        assignmentId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${clickedFrom}:assignmentLimitPaywallDialog_${action}`,
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User clicks one of buttons or links from the assignment limit paywall
   *
   * */
  clickAssignmentLimitPaywallButtonOrLink(
    buttonVariation,
    assignmentCount,
    isFromDisabledAssignment,
    assignmentId
  ) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const type = buttonVariation === 'proTeacher' ? 'proTeacher' : 'proSchool';
    const eventInfo = {
      eventTag: `assignmentLimitPaywallDialog:${type}_clicked`,
      properties: {
        userId,
        assignmentCount,
        isFromDisabledAssignment,
        assignmentId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`assignmentLimitPaywallDialog:${type}_clicked`,
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User clicks on one of the disabled assignments in their assignment list
   *
   * */
  clickOnDisabledAssignment(assignmentCount, assignmentId) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: 'assignmentList:disabledAssignment_clicked',
      properties: {
        userId,
        assignmentCount,
        assignmentId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('assignmentList:disabledAssignment_clicked',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User adds assignment to roster
   *
   */
  addAssignmentToRoster(clickedFrom, assignmentId, rosterId,) {
    let eventInfo = {
      eventTag: `${clickedFrom}:addAssignmentToRoster_clicked`,
      properties: {
        assignmentId,
        rosterId
      },
      count: true
    };

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `${clickedFrom}:addAssignmentToRoster_clicked`,
      {
        ...eventInfo.properties
      },
      true
    );
  }

  /**
   * User removes assignment from roster
   *
   */
  removeAssignmentFromRoster(clickedFrom, assignmentId, rosterId,) {
    let eventInfo = {
      eventTag: `${clickedFrom}:removeAssignmentFromRoster_clicked`,
      properties: {
        assignmentId,
        rosterId
      },
      count: true
    };

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `${clickedFrom}:removeAssignmentFromRoster_clicked`,
      {
        ...eventInfo.properties
      },
      true
    );
  }

  /**
   * User goes to assignment view work
   *
   */
  goToAssignmentViewWork(clickedFrom, assignmentId, rosterId,) {
    let eventInfo = {
      eventTag: `${clickedFrom}:assignmentName_clicked`,
      properties: {
        assignmentId,
        rosterId
      },
      count: true
    };

    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `${clickedFrom}:assignmentName_clicked`,
      {
        ...eventInfo.properties
      },
      true
    );
  }


  /**
   * Incentive analytics
   *
   * @param event_name
   * @param incentive_id
   */
  incentiveSend(event_name, incentive_id) {
    this._mixpanelService.send(event_name,
      {
        incentiveId: incentive_id
      },
      true
    );
  }

  closeFitb(format) {
    const eventInfo = {
      eventTag: 'asmt:fitb_closed',
      properties: {
        format
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('asmt:fitb_closed',
      {
        ...eventInfo.properties
      }, true);
  }

  shareWorkDialogOpened(location) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: `${location}:shareWorkDialog_opened`,
      properties: {
        userId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${location}:shareWorkDialog_opened`,
      {
        ...eventInfo.properties
      }, true);
  }

  accountCreatedFromSharedWork(authType) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: 'sharedWork:account_created',
      properties: {
        userId,
        authType
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('sharedWork:account_created',
      {
        ...eventInfo.properties
      }, true);
  }

  sharedWorkOpened() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: 'sharedWork:work_opened',
      properties: {
        userId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('sharedWork:work_opened',
      {
        ...eventInfo.properties
      }, true);
  }
  startClasskickIntroVideo() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: 'nav:classkickIntroVideo_started',
      properties: {
        userId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('nav:classkickIntroVideo_started',
      {
        ...eventInfo.properties
      }, true);
  }

  closeClasskickIntroVideoDialog() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: 'nav:classkickIntroVideo_closed',
      properties: {
        userId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('nav:classkickIntroVideo_closed',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * Teacher sends invite to co-teacher
   * @param rosterId {string}
   * */
  inviteCoTeacher(rosterId) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: 'roster:coteacher_invited',
      properties: {
        userId,
        rosterId,
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('roster:coteacher_invited',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * Teacher clicks into a co-taught roster
   * @param rosterId {string}
   * @param userId {string}
   * @param teacherUser {string}
   * */
  openCoTeachingRoster(rosterId, userId, teacherUser) {
    const eventInfo = {
      eventTag: 'roster:coteacher_rosterClicked',
      properties: {
        userId,
        rosterId,
        teacherUser
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('roster:coteacher_rosterClicked',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * Teacher opens WCV for assignment from roster page
   * @param rosterId {string}
   * @param userId {string}
   * @param teacherUser {string}
   * */
  openViewWorkForCoTeachingRoster(rosterId, userId, teacherUser) {
    const eventInfo = {
      eventTag: 'roster:coteacher_viewWork',
      properties: {
        userId,
        rosterId,
        teacherUser
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('roster:coteacher_viewWork',
      {
        ...eventInfo.properties
      }, true);
  }

  sendUniversalCtaEvent(page, userId, buyerState, metric, action = false) {
    const activity = action ? 'clicked' : 'shown';
    const eventInfo = {
      eventTag: `${metric}_${activity}`,
      properties: {
        userId,
        buyerState,
        page
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(`${metric}_${activity}`,
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher sends a gradebook invite to their admin. Properties include {”userId”: String, “adminEmail”: String, “numberOfAttempts”: Int}
    * @param userId {string}
    * @param adminEmail {string}
    * @param numberOfAttempts {int}
  **/
  gradebookAdminAccessInviteSent(userId, adminEmail, numberOfAttempts) {
    const eventInfo = {
      eventTag: 'gradebook_adminInviteSent',
      properties: {
        userId,
        adminEmail,
        numberOfAttempts
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_adminInviteSent',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher updates their unassociated email from the current email. Properties {”userId”: String}
    * @param userId {string}
  **/
  gradebookAuthIssueUnassociatedEmailUpdated(userId) {
    const eventInfo = {
      eventTag: 'gradebook_unassociatedEmailUpdated',
      properties: {
        userId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_unassociatedEmailUpdated',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher manually links a class to a classkick roster that is different than current. Properties include {”userId”: String}
    * @param userId {string}
  **/
  gradebookOverviewClassLinked(userId) {
    const eventInfo = {
      eventTag: 'gradebook_classLinked',
      properties: {
        userId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_classLinked',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher manually links a classkick student to an LMS student. Properties include {”userId”: String}
    * @param userId {string}
  **/
  gradebookOverviewStudentLinked(userId) {
    const eventInfo = {
      eventTag: 'gradebook_studentLinked',
      properties: {
        userId
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_studentLinked',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher submits grades to their LMS. Properties include {”userId”: String, ”forWholeRoster”: Bool, "exportCount": Number} representing whether the user is submitting the whole roster’s grades or just the partial roster.
    * @param userId {string}
    * @param assignmentId {string}
    * @param rosterId {string}
    * @param categoryId {string|null}
    * @param dueDate {string|null}
    * @param forWholeClass {boolean}
    * @param exportCount {number}
  **/
  gradebookGradesSubmitted(userId, assignmentId, rosterId, categoryId, dueDate, forWholeClass, exportCount) {
    const eventInfo = {
      eventTag: 'gradebook_submitted',
      properties: {
        userId,
        assignmentId,
        rosterId,
        categoryId,
        dueDate,
        forWholeClass,
        exportCount
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_submitted',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher experienced a grade submission failure to their LMS. Properties include {”userId”: String, ”forWholeRoster”: Bool, "exportCount": Number} representing whether the user is submitting the whole roster’s grades or just the partial roster.
    * @param userId {string}
    * @param assignmentId {string}
    * @param rosterId {string}
    * @param categoryId {string|null}
    * @param dueDate {string|null}
    * @param forWholeClass {boolean}
    * @param exportCount {number}
  **/
  gradebookGradesSubmissionFailure(userId, assignmentId, rosterId, categoryId, dueDate, forWholeClass, exportCount) {
    const eventInfo = {
      eventTag: 'gradebook_submission_failure',
      properties: {
        userId,
        assignmentId,
        rosterId,
        categoryId,
        dueDate,
        forWholeClass,
        exportCount
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_submission_failure',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher dismissed the export grades modal.
    * @param location {string}
  **/
  gradebookOpened(location) {
    const eventInfo = {
      eventTag: 'gradebook_opened',
      properties: {
        location
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_opened',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
    * Teacher clicked the export grades button.
  **/
  gradebookDismissed() {
    const eventInfo = {
      eventTag: 'gradebook_dismissed',
      properties: {
      },
      count: true
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('gradebook_dismissed',
      {
        ...eventInfo.properties
      }, true);
  }

  /**
   * User taps the Import from a Google Drive button
   */
  googleDriveOpened() {
    if (!this.isLoggedIn && !this.isTeacher) {
      const userId = this._userData ? this._userData.id : 'anonymous';
      const eventInfo = {
        eventTag: 'googleDriveOpened_clicked',
        properties: {
          userId: userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('googleDriveOpened_clicked', {
      userId: this._userData ? this._userData.id : 'anonymous'
    }, true);
  }

  /**
   * Error importing file from Google Drive
   * @param fileType {string}
   */
  errorImportingFileFromGoogleDrive(fileType){
    if (!this.isLoggedIn && !this.isTeacher) {
      const userId = this._userData ? this._userData.id : 'anonymous';
      const userEmail = this._userData ? this._userData.email : 'n/a';
      const eventInfo = {
        eventTag: 'googeDriveFileImport_error',
        properties: {
          userId: userId,
          userEmail: userEmail,
          fileType: fileType
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('googeDriveFileImport_error', {
      userId: this._userData ? this._userData.id : 'anonymous', 
      userEmail: this._userData ? this._userData.email : 'n/a',
      fileType: fileType
    }, true);
  }

  /**
   * Success importing file from Google Drive
   * @param fileType {string}
   */
  successImportingFileFromGoogleDrive(fileType){
    if (!this.isLoggedIn && !this.isTeacher) {
      const userId = this._userData ? this._userData.id : 'anonymous';
      const userEmail = this._userData ? this._userData.email : 'n/a';
      const eventInfo = {
        eventTag: 'googeDriveFileImport_success',
        properties: {
          userId: userId,
          userEmail: userEmail,
          fileType: fileType
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('googeDriveFileImport_success', {
      userId: this._userData ? this._userData.id : 'anonymous', 
      userEmail: this._userData ? this._userData.email : 'n/a',
      fileType: fileType
    }, true);
  }

  /**
   * User taps the dropdownbutton on the sidebar
   */
  sidebarDropdownButtonOpenClicked(){
    if (!this.isLoggedIn && !this.isTeacher) {
      const userId = this._userData ? this._userData.id : 'anonymous';
      const eventInfo = {
        eventTag: 'sideBarDropDownOpen_clicked',
        properties: {
          userId: userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('sideBarDropDownOpen_clicked', {
      userId: this._userData ? this._userData.id : 'anonymous'
    }, true);
  }

  sidebarDropdownButtonCloseClicked(){
    if (!this.isLoggedIn && !this.isTeacher) {
      const userId = this._userData ? this._userData.id : 'anonymous';
      const eventInfo = {
        eventTag: 'sideBarDropDownClose_clicked',
        properties: {
          userId: userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('sideBarDropDownClose_clicked', {
      userId: this._userData ? this._userData.id : 'anonymous'
    }, true);
  }

  studentJoinedWithClassCode(){
    if (!this.isLoggedIn && !this.isTeacher) {
      const userId = this._userData ? this._userData.id : 'anonymous';
      const eventInfo = {
        eventTag: 'studentAddedToClass_classCode',
        properties: {
          userId: userId
        },
        count: true
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send('studentAddedToClass_classCode', {
      userId: this._userData ? this._userData.id : 'anonymous'
    }, true);
  }
}


